/**
 * The harness client, booted inside Orik's own page.
 *
 * Their shell is normally a page: an `index.html` renders the boot injection table, preloads the module
 * system, and `AppWebEntry` takes `#root`. Orik's page is its own (sign-in comes first), so this entry does
 * the same work once the user is signed in: it applies the injection table, registers the module system and
 * Orik's own boot entry, and hands `AppWebEntry` the element the app gives it. From there the page is the
 * harness's: its boot page while the plugins activate, then its frame — sidebar, conversation and right
 * sidebar — with Orik's occupants in their seats (`harness/orik-harness.ts`).
 *
 * The returned promise is the caller's signal; the boot happens once per page.
 */
import { AppWebEntry, applyIndexInjections, type BootSeams } from "@deepseek-ai/dsh-client-web";
import type { ClientBoot } from "@vexxhost/dsh-runtime";
import { installHarnessTransport } from "../src/lib/agent/harness-transport";
import { apply as installOrikClient } from "./orik-client";
import { registerOrikHarness } from "./orik-harness";

/**
 * The boot table: the roster whose bundles the page will fetch, and the index rows that start it.
 *
 * Composed by the harness's own `ClientModuleRegistry` on the host (`src/server/client-modules.ts`, and the
 * same composition under a dev server), which also serves every URL it names.
 */
export type DshClientBootTable = ClientBoot;

/**
 * Where the host serves the boot table (`CLIENT_BOOT_PATH` in `@vexxhost/dsh-runtime`), below the page's root.
 *
 * Fetched rather than built in: its URLs carry the host's bundle revisions. The document preloads it, so the
 * request is already under way when this chunk asks.
 */
const BOOT_TABLE_PATH = "/plugins/boot.json";

/** Fetch the host's boot table. */
async function fetchBootTable(): Promise<DshClientBootTable> {
  const response = await fetch(new URL(BOOT_TABLE_PATH, document.baseURI));
  if (!response.ok) throw new Error(`dsh client boot: the host answered ${String(response.status)} for its boot table`);
  return await response.json() as DshClientBootTable;
}

export interface DshClientHandle {
  /**
   * The booted Cordis context: the kernel the client plugins were applied to.
   *
   * This is the handle a consumer needs — `sessions` is the client session layer, and it
   * is reached through the context rather than through the shell, whose class keeps the
   * same field private. A deployment that owns its own client plugin takes the context in
   * that plugin's `apply` instead; until this repository ships one, this is the reach.
   */
  readonly context: unknown;
  /** The boot table this client was built from. */
  readonly table: DshClientBootTable;
  /** Dispose the plugin tree. */
  dispose(): Promise<void>;
}

export interface DshClientBootOptions {
  /** Test seam: the boot table, instead of the host's. */
  readonly table?: DshClientBootTable;
  /** Test seam: their bundle transport, replaced where no plugin broker is reachable. */
  readonly seams?: BootSeams;
  /** The element the harness renders into. */
  readonly mount: HTMLElement;
}

let started: Promise<DshClientHandle> | undefined;

/**
 * Boot the harness client, once per page.
 *
 * @param options - test seams; a deployment passes nothing.
 * @returns the booted client, or a rejection carrying why it did not come up.
 */
export function bootDshClientInApp(options: DshClientBootOptions): Promise<DshClientHandle> {
  // Single-flight: the kernel is a singleton per document, and a second boot would
  // register every bundle twice, which their loader rejects as a duplicate.
  started ??= boot(options);
  return started;
}

async function boot(options: DshClientBootOptions): Promise<DshClientHandle> {
  // Before anything loads: the connection plugin reads this global while it applies, and
  // without it their client posts to `/api` — which is Orik's own routes, not theirs.
  installHarnessTransport();
  const table = options.table ?? await fetchBootTable();
  const { mount } = options;
  await applyIndexInjections(table.injections, loadScript);
  // Since dsh 0.2.2 a booted package (`ui-settings-general`) injects the module system, so the boot table
  // delivers its bundle as a `script-src` row and the injections above have just registered it. Registering
  // Orik's bundled copy as well would be a duplicate registration, which their loader rejects.
  if (!table.injections.some(row => row.kind === "script-src" && row.src.includes(MODULE_SYSTEM_BUNDLE))) await registerModuleSystem();
  // Before the kernel starts: Orik's frame and the services the conversation panel waits on are a boot
  // entry, applied during boot, so the kernel never mounts without a `root` and no entry waits forever.
  registerOrikHarness();
  // Their entry draws a framework-free boot page into the mount (a spinner whose arc grows as plugins activate,
  // and the name and reason of any plugin that fails) and hydrates it into the app once every entry is active.
  const entry = new AppWebEntry(mount, options.seams);
  let failure: unknown;
  await entry.run((reason: unknown) => {
    failure = reason;
    console.error("dsh client boot failed", reason);
  });
  if (failure !== undefined) throw failure instanceof Error ? failure : new Error(String(failure));
  const context = (entry as unknown as { ctx?: unknown }).ctx;
  if (context === undefined) throw new Error("dsh client boot: the shell booted without exposing a kernel context");
  // Orik's own client plugin, installed with the context this boot produced rather than as a
  // manifest entry: it has no lifecycle the kernel has to own, and their entry reconciliation
  // reports a failure as an unresolvable random id — a diagnosis nobody can act on.
  installOrikClient(context as Parameters<typeof installOrikClient>[0]);
  return {
    context,
    table,
    async dispose(): Promise<void> {
      await entry.dispose();
    },
  };
}

/** The module system's bundle path inside a boot-table URL. */
const MODULE_SYSTEM_BUNDLE = "@deepseek-ai/dsh-client-modules/client.js";

/**
 * Their asset transport for a bootstrap script, mirroring their own default loader.
 *
 * The one `script-src` row in this deployment's graph is the module system's own bundle; the rest of the
 * roster is application batches, which the kernel fetches itself through the bundle transport.
 */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const element = document.createElement("script");
    element.async = true;
    element.src = src;
    element.addEventListener("load", () => { element.remove(); resolve(); }, { once: true });
    element.addEventListener("error", () => {
      element.remove();
      reject(new Error(`dsh client boot: the bootstrap script ${src} failed to load`));
    }, { once: true });
    document.head.append(element);
  });
}

/**
 * Register the module system's own bundle.
 *
 * The queue facade the injection table installs looks for this registration by id and
 * throws without it — a served page satisfies that with a preload, and this code is the
 * page. The bundle registers *itself* with `window.__ModuleLoader__.load` as it evaluates,
 * so importing it is the registration; there is nothing to hand over afterwards.
 *
 * The import is inside the function on purpose. A top-level import would evaluate the
 * bundle while this module loads, before the injection table has installed that facade —
 * which is a `TypeError` on `undefined.load`, not a missing registration, and the reason
 * their own build treats these bundles as preloaded scripts.
 */
async function registerModuleSystem(): Promise<void> {
  const loader = (globalThis as { __ModuleLoader__?: unknown }).__ModuleLoader__;
  if (loader === undefined) throw new Error("dsh client boot: the boot table did not install the module loader");
  await import("@deepseek-ai/dsh-client-modules/client");
}
